import { isLicensePlate } from "@edaz/services/validators/isLicensePlate";
import { interfaces } from "inversify";
import { action, computed, observable, reaction, toJS, runInAction } from "mobx";
import LocalizationService from "services/localizationService";
import ChargeTypesService from "services/chargeTypesService";
import ChargeTypeSelectViewModel from "../common/chargeTypeSelectViewModel";
import FuelTypeSelectViewModel from "../common/fuelTypeSelectViewModel";
import VehicleInfoViewModel from "../common/vehicleInfoViewModel";
import { attachAutomaticBlurValidator, setVisitedProperty } from "utils/validationPatch";
import { ORDER_ITEM_MAX_COUNT } from "../../config";
import { ScreenBase } from "@frui.ts/screens";
import ValiditySelectViewModel from "../eshop/order/components/validitySelectViewModel";
import { dateStringWithServerZone, isSameOrBeforePragueDay, maxValidSince, zonedCZTimeToUtc } from "utils/dateUtils";
import EnumService from "services/enumService";
import ChargeValidityRuleResponse from "entities/chargeValidityRuleResponse";
import ChargeOrderItem, { ChargeOrderItemBatchValidationRules } from "models/chargeOrderItem";
import ChargeOrderService from "services/chargeOrderService";
import AppRouter from "services/appRouter";
import { getStatusSteps } from "./multiEshopStatusSteps";
import ChargeValidity, { validityDays } from "entities/chargeValidity";
import { addDays, areIntervalsOverlapping, subDays, startOfDay, endOfDay, format } from "date-fns";
import TimeService from "services/timeService";
import { endOfCzechDay, dateToIsoDate } from "../../utils/dateUtils";

export default class BatchOrderItemViewModel extends ScreenBase {
  private unsubscribes: Array<() => void> = [];

  public vehicleInfoViewModel: VehicleInfoViewModel;
  public validitySelectViewModel: ValiditySelectViewModel;
  public chargeTypeSelectViewModel: ChargeTypeSelectViewModel;
  public fuelTypeSelectViewModel: FuelTypeSelectViewModel;
  statusNavigation = getStatusSteps();
  ta = (code: string) => this.localizationService.translateAttribute("orderItem", code);

  @observable validSinceDaysBefore?: Date;
  @observable isBeforePosibble?: boolean;
  @observable isSingleCharTypeToday?: boolean;

  constructor(
    public chargeOrderItem: ChargeOrderItem,
    public resetChargeOrder: () => void,
    public enumService: EnumService,
    public localizationService: LocalizationService,
    private chargeOrderService: ChargeOrderService,
    private timeService: TimeService,
    private chargeTypesService: ChargeTypesService,
    private router: AppRouter,
    chargeTypeSelectViewModelFactory: ReturnType<typeof ChargeTypeSelectViewModel.Factory>,
    vehicleInfoModelFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    validitySelectViewModelFactory: ReturnType<typeof ValiditySelectViewModel.Factory>,
    fuelTypeSelectViewModelFactory: ReturnType<typeof FuelTypeSelectViewModel.Factory>
  ) {
    super();
    this.validitySelectViewModel = validitySelectViewModelFactory(this.chargeOrderItem);
    this.validitySelectViewModel.getPostponedValidityMaxDays(
      this.serverTime,
      this.enumService.values("charge_validity_rules") as ChargeValidityRuleResponse[]
    );
    this.chargeTypeSelectViewModel = chargeTypeSelectViewModelFactory(this.chargeOrderItem, this.chargeOrderItem.id);
    this.fuelTypeSelectViewModel = fuelTypeSelectViewModelFactory(this.chargeOrderItem);
    this.vehicleInfoViewModel = vehicleInfoModelFactory(this.chargeOrderItem);
    const serverTime = this.serverTime;
    const dateFormat = "yyyy-MM-dd";
    attachAutomaticBlurValidator(this.chargeOrderItem, {
      ...ChargeOrderItemBatchValidationRules,
      validSince: {
        required: true,
        dateRange: {
          min: zonedCZTimeToUtc(format(serverTime, dateFormat)) ?? new Date(),
          max: zonedCZTimeToUtc(
            format(
              maxValidSince(serverTime, this.validitySelectViewModel.postponedValidityRule?.postponedValidityMaxDays),
              dateFormat
            )
          ),
        },
      },
    });
  }

  protected async onInitialize(): Promise<Promise<any> | void> {
    await this.timeService.getCurrentServerTime();
    this.unsubscribes.push(
      reaction(() => this.validitySelectViewModel.chargeOrderChargeItem.validSince, this.onValidSinceChanged)
    );
    this.unsubscribes.push(
      reaction(
        () => [this.vehicleInfoViewModel.item.countryId, this.chargeOrderItem.licensePlates.length],
        this.onVehicleInfoChanged
      )
    );
    this.unsubscribes.push(reaction(() => this.fuelTypeSelectViewModel.chargeOrderFuelItem.fuelTypeId, this.onFuelTypeIdChanged));
    this.unsubscribes.push(
      reaction(() => this.chargeTypeSelectViewModel.chargeOrderChargeItem.chargeTypeId, this.onChargeTypeIdChanged)
    );
    // Force recomputation of overlap detection when any item in the array changes
    this.unsubscribes.push(
      reaction(
        () => [
          this.chargeOrderService.chargeOrderItems.length,
          ...this.chargeOrderService.chargeOrderItems.map(item => [
            item.licensePlates.slice(),
            item.validSince,
            item.chargeTypeId,
            item.countryId,
          ]),
        ],
        () => {
          // Force recomputation by accessing the computed property
          runInAction(() => {
            const hasOverlay = this.licensePlatesHasOverlay;
          });
        }
      )
    );
    return super.onInitialize();
  }

  protected onActivate() {
    this.singleCharTypeToday();
    document.addEventListener("mousedown", this.clickOutside);
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    document.removeEventListener("mousedown", this.clickOutside);
    if (isClosing) {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
      this.unsubscribes = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get renderNumberOfLicensePlates() {
    return this.chargeOrderItem.licensePlates.length > 0 ? this.chargeOrderItem.licensePlates.length : null;
  }

  @computed
  get licensePlates() {
    return this.chargeOrderItem.licensePlates;
  }

  @computed
  get licensePlatesHasOverlay() {
    const result = this.chargeOrderItem.licensePlates.find(item =>
      this.otherLicensePlatesWithValidityIntersection.includes(item)
    );
    return !!result;
  }

  @computed
  get showInfoAboutVehicles() {
    const storageItem = this.storedChargeOrderItem();
    const countryId = storageItem?.countryId;
    const isCzech = this.enumService.values("countries").find(country => country.code === "CZ" && country.id === countryId);
    return (
      this.enumService.values("countries").length > 0 && !this.chargeOrderItem._fuelTypeLimitation?.limitationReason && !isCzech
    );
  }

  @computed
  get licensePlatesErrorMessage() {
    const errorMessages = [];
    if (this.licensePlatesHasOverlay) {
      errorMessages.push(this.localizationService.translateGeneral("multiEshop.licensePlatesOverlayWarning"));
    }

    if (this.chargeOrderItem.licensePlates.some(licensePlate => !isLicensePlate(licensePlate).isValid)) {
      errorMessages.push(this.localizationService.translate("validators.isLicensePlate.licensePlateTooLong"));
    }

    return errorMessages.length ? errorMessages.join(" ") : undefined;
  }

  @computed
  get otherLicensePlatesWithValidityIntersection() {
    // Force tracking of all relevant observables for proper dependency tracking
    const dummy = this.chargeOrderService.chargeOrderItems.map(item => ({
      id: item.id,
      licensePlates: item.licensePlates.slice(),
      validSince: item.validSince,
      validUntil: item.validity._validUntilOverlap,
      countryId: item.countryId,
    }));

    const thisValidSince = this.chargeOrderItem.validity.hasImmediateValidity
      ? this.serverTime
      : this.chargeOrderItem.validity.validSinceDate;
    const thisValidUntil = this.chargeOrderItem.validity._validUntilOverlap;
    const countryId = this.chargeOrderItem.countryId;

    if (!countryId || !thisValidSince || !thisValidUntil) return [];

    const otherChargeOrderItemsWithSameCountry = this.chargeOrderService.chargeOrderItems.filter(
      item => item.id !== this.chargeOrderItem.id && item.countryId == countryId
    );

    return otherChargeOrderItemsWithSameCountry
      .filter(item => {
        if (!item.validity.validSinceDate || !item.validity._validUntilOverlap) {
          return false;
        }
        const thisValidityInterval = {
          start: new Date(dateToIsoDate(thisValidSince)),
          end: new Date(dateToIsoDate(thisValidUntil)),
        };
        const itemValidityInterval = {
          start: new Date(dateToIsoDate(item.validity.validSinceDate)),
          end: new Date(dateToIsoDate(item.validity._validUntilOverlap)),
        };
        return areIntervalsOverlapping(thisValidityInterval, itemValidityInterval, { inclusive: true });
      })
      .flatMap(item => toJS(item.licensePlates));
  }

  @computed
  get canAddOrderItem() {
    const index = this.chargeOrderService.chargeOrderItems.findIndex(item => item.id === this.chargeOrderItem.id);
    return index === this.chargeOrderService.chargeOrderItems.length - 1 && index + 1 < ORDER_ITEM_MAX_COUNT;
  }

  @action.bound
  storedChargeOrderItem(id?: string) {
    return this.chargeOrderService.chargeOrderItems.find(item => item.id === (id ?? this.chargeOrderItem.id));
  }

  @action.bound
  singleCharTypeToday() {
    this.isSingleCharTypeToday = this.chargeOrderItem.validSince
      ? isSameOrBeforePragueDay(this.chargeOrderItem.validSince, this.serverTime) &&
        this.chargeTypesService.findChargeType(this.chargeOrderItem.chargeTypeId)?.validFor === ChargeValidity.SingleDay
      : false;
  }

  @action.bound
  clickOutside() {
    // Inside of the view model, for some reason the onActivate doesn't get triggered.
    this.fuelTypeSelectViewModel.clickOutside();
  }

  licensePlateHasOverlay = (licensePlate: string) => this.otherLicensePlatesWithValidityIntersection.includes(licensePlate);

  @action.bound
  licensePlatesFieldVisited() {
    setVisitedProperty(this.chargeOrderItem, "licensePlates");
  }

  @action.bound
  toggleChargeOrderItem = (chargeOrderItem: ChargeOrderItem) => () => (chargeOrderItem._visible = !chargeOrderItem._visible);

  @action.bound
  private updateChargeOrderItem() {
    const storageItem = this.storedChargeOrderItem();
    // We must set validUntil, so the multiEshop can process overlap conflicts.
    this.chargeOrderItem.validity._validUntilOverlap = this.chargeOrderItem._validUntilOverlap = this.setTemporaryValidUntil(
      this.chargeOrderItem
    );
    if (!storageItem) return;
    if (this.chargeOrderItem.chargeTypeId && this.chargeOrderItem.fuelTypeId) {
      const priceListId = this.chargeTypesService.chargePriceList?.items?.find(
        item => item.chargeTypeId === this.chargeOrderItem.chargeTypeId && item.fuelTypeId === this.chargeOrderItem.fuelTypeId
      )?.id;
      if (priceListId) {
        this.chargeOrderItem.priceListItemId = priceListId;
        storageItem.priceListItemId = priceListId;
      }
    }
    if (this.chargeOrderItem.fuelTypeId) {
      storageItem._fuelTypeId = storageItem.fuelTypeId = this.fuelTypeSelectViewModel.chargeOrderFuelItem.fuelTypeId;
    }
    if (this.chargeOrderItem.licensePlates.length > 0) {
      storageItem.licensePlates = this.chargeOrderItem.licensePlates;
    }
    if (this.chargeOrderItem.validSince && isSameOrBeforePragueDay(this.chargeOrderItem.validSince, this.serverTime)) {
      storageItem._hasImmediateValidity = this.chargeOrderItem._hasImmediateValidity = true;
    } else {
      storageItem._hasImmediateValidity = this.chargeOrderItem._hasImmediateValidity = false;
    }

    storageItem.validity.validSinceDate = storageItem.validSince = this.chargeOrderItem.validSince;
    storageItem.chargeTypeId = this.chargeOrderItem.chargeTypeId;
    storageItem._singleChargeType =
      this.chargeTypesService.findChargeType(this.chargeOrderItem.chargeTypeId)?.validFor === ChargeValidity.SingleDay;
    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  setTemporaryValidUntil(chargeOrderItem: ChargeOrderItem) {
    if (!chargeOrderItem.validSince || !chargeOrderItem._chargeTypeId) {
      return undefined;
    }
    const chargeType = this.chargeTypesService.validChargeTypes.find(x => x.id === chargeOrderItem.chargeTypeId);
    if (!chargeType) {
      return undefined;
    }
    if (chargeType?.validFor === ChargeValidity.SingleDay) {
      if (isSameOrBeforePragueDay(chargeOrderItem.validSince, this.serverTime)) {
        return endOfDay(this.serverTime);
      }
      return endOfDay(chargeOrderItem.validSince);
    }
    return endOfDay(addDays(chargeOrderItem.validSince, validityDays(chargeType?.validFor) - 1));
  }

  @action.bound
  addItem(newLicensePlate: string) {
    if (this.chargeOrderItem.licensePlates.some(licensePlate => licensePlate === newLicensePlate)) {
      this.chargeOrderItem._duplicities++;
      return;
    }

    this.chargeOrderItem.licensePlates.push(newLicensePlate);
    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  removeItem(item: string) {
    this.chargeOrderItem.licensePlates = this.chargeOrderItem.licensePlates.filter(licensePlate => licensePlate !== item);
    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  private onValidSinceChanged() {
    this.chargeOrderItem.validity.validSinceDate = this.chargeOrderItem.validSince =
      this.validitySelectViewModel.chargeOrderChargeItem.validSince;
    this.chargeOrderItem._isModified = true;
    this.singleCharTypeToday();
    this.updateChargeOrderItem();
    this.chargeOrderService.updatePaymentMethod();
  }

  @action.bound
  private onChargeTypeIdChanged() {
    this.chargeOrderItem.chargeTypeId = this.chargeOrderItem._chargeTypeId =
      this.chargeTypeSelectViewModel.chargeOrderChargeItem.chargeTypeId;
    this.chargeOrderItem._isModified = true;
    this.singleCharTypeToday();
    this.updateChargeOrderItem();
  }

  @action.bound
  private onVehicleInfoChanged() {
    this.chargeOrderItem._isModified = true;
    this.updateChargeOrderItem();
  }

  @action.bound
  private onFuelTypeIdChanged() {
    const storageItem = this.storedChargeOrderItem(this.chargeOrderItem.id);
    if (storageItem) {
      storageItem._fuelTypeId = this.chargeOrderItem._fuelTypeId = this.fuelTypeSelectViewModel.chargeOrderFuelItem.fuelTypeId;
      storageItem._priceListItems = this.chargeTypesService.chargePriceList?.items.filter(
        item => item.fuelTypeId === this.fuelTypeSelectViewModel.chargeOrderFuelItem.fuelTypeId
      );
    }
    this.chargeOrderItem.fuelTypeId = this.chargeOrderItem._fuelTypeId =
      this.fuelTypeSelectViewModel.chargeOrderFuelItem.fuelTypeId;
    this.chargeOrderItem._isModified = true;
    this.updateChargeOrderItem();
  }

  @action.bound
  translateInfoAboutVehicle(code: string) {
    return this.translate(`infoAboutVehicle.${code}`);
  }

  @action.bound
  translate = (code: string) =>
    this.localizationService.translateGeneral(
      `multiEshop.batchPage.${code}`,
      undefined,
      true // Important for markdown links to function correctly
    );

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderItem: ChargeOrderItem, resetChargeOrder: () => void) => {
      return new BatchOrderItemViewModel(
        chargeOrderItem,
        resetChargeOrder,
        container.get(EnumService),
        container.get(LocalizationService),
        container.get(ChargeOrderService),
        container.get(TimeService),
        container.get(ChargeTypesService),
        container.get(AppRouter),
        container.get(ChargeTypeSelectViewModel.Factory),
        container.get(VehicleInfoViewModel.Factory),
        container.get(ValiditySelectViewModel.Factory),
        container.get(FuelTypeSelectViewModel.Factory)
      );
    };
  }
}
